/**
 * HR Policy–style print for all policies (same pattern as PaceHRM PolicySummaryModal).
 * Columns: Description | Policy 1 | Policy 2 | ...
 */

function formatPrintValue(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function escapeHtml(s) {
  if (s == null) return "";
  const str = String(s);
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * @param {string} policyTypeLabel - e.g. "Confirmation Policy"
 * @param {Array<Object>} policies
 * @param {(policy: Object) => Array<{ label: string, value: any }>} getRowsForPolicy
 */
export function printAllPoliciesAsDocument(policyTypeLabel, policies, getRowsForPolicy) {
  const list = Array.isArray(policies) ? policies : [];
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    window.print();
    return;
  }

  if (!list.length) {
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>${escapeHtml(policyTypeLabel)} - All Policies</title></head>
      <body style="font-family:&quot;Segoe UI&quot;,Inter,Arial,sans-serif;padding:24px;"><div class="policy-summary-print-header"><h1>${escapeHtml(policyTypeLabel)}</h1><div class="sub">All policies — No policies configured</div></div></body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    return;
  }

  const getRows =
    typeof getRowsForPolicy === "function"
      ? getRowsForPolicy
      : (p) => [
          { label: "Policy name", value: p.policyName },
          { label: "Effective date", value: p.effectiveDate },
        ];

  const allRows = list.map((p) => getRows(p));
  const labelOrder = [];
  const labelSeen = new Set();
  allRows.forEach((rows) => {
    (rows || []).forEach((r) => {
      if (r && r.label && !labelSeen.has(r.label)) {
        labelSeen.add(r.label);
        labelOrder.push(r.label);
      }
    });
  });
  const valueByPolicyAndLabel = list.map((p, idx) => {
    const map = {};
    (allRows[idx] || []).forEach((r) => {
      if (r && r.label != null) map[r.label] = r.value;
    });
    return map;
  });

  const thStyle =
    "font-weight:600;color:#475569;background:#f8fafc;padding:10px 12px;border-bottom:1px solid #e2e8f0;text-align:left;vertical-align:top;";
  const tdStyle = "padding:10px 12px;border-bottom:1px solid #f1f5f9;color:#1e293b;vertical-align:top;";
  const descCellStyle = "font-weight:500;color:#475569;width:220px;min-width:180px;";

  const headerCells = [
    `<th style="${thStyle}${descCellStyle}">Description</th>`,
    ...list.map(
      (p) =>
        `<th style="${thStyle}">${escapeHtml(p.policyName || "—")}<br/><span style="font-size:11px;color:#64748b;">Effective: ${escapeHtml(p.effectiveDate || "—")}</span></th>`
    ),
  ].join("");

  const bodyRows = labelOrder
    .map((label) => {
      const cells = [
        `<td style="${tdStyle}${descCellStyle}">${escapeHtml(label)}</td>`,
        ...valueByPolicyAndLabel.map(
          (map) => `<td style="${tdStyle}">${escapeHtml(formatPrintValue(map[label]))}</td>`
        ),
      ].join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(policyTypeLabel)} - All Policies</title>
        <style>
          body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; padding: 24px; max-width: 95%; margin: 0 auto; }
          .policy-summary-print-header { border-bottom: 2px solid #1e293b; padding-bottom: 12px; margin-bottom: 20px; }
          .policy-summary-print-header h1 { margin: 0; font-size: 18px; font-weight: 700; }
          .policy-summary-print-header .sub { margin-top: 4px; font-size: 11px; color: #64748b; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { word-wrap: break-word; }
          @media print { body { padding: 16px; } table { font-size: 11px; } }
        </style>
      </head>
      <body>
        <div class="policy-summary-print-header">
          <h1>${escapeHtml(policyTypeLabel)}</h1>
          <div class="sub">All policies — Policy document (Description and configurations per policy)</div>
        </div>
        <table>
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}

/**
 * Single-policy print in the same HR policy layout as PolicySummaryModal (label/value, not a raw table dump).
 */
export function printSinglePolicyAsDocument(
  policyTypeLabel,
  policyName,
  effectiveDate,
  getRowsForPolicy,
  policy
) {
  const rows =
    typeof getRowsForPolicy === "function" ? getRowsForPolicy(policy) : [];
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    window.print();
    return;
  }

  const bodyRows = (rows || [])
    .map(
      (r) => `
      <div class="policy-summary-row">
        <span class="policy-summary-label">${escapeHtml(r.label)}</span>
        <span class="policy-summary-value">${escapeHtml(formatPrintValue(r.value))}</span>
      </div>`
    )
    .join("");

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(policyTypeLabel)} — ${escapeHtml(policyName || "Policy")}</title>
        <style>
          body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; padding: 24px; max-width: 800px; margin: 0 auto; }
          .policy-summary-print-header { border-bottom: 2px solid #1e293b; padding-bottom: 12px; margin-bottom: 20px; }
          .policy-summary-print-header h1 { margin: 0; font-size: 18px; font-weight: 700; }
          .policy-summary-print-header .sub { margin-top: 4px; font-size: 11px; color: #64748b; }
          .policy-summary-row { display: flex; padding: 6px 0; border-bottom: 1px solid #f1f5f9; }
          .policy-summary-row:last-child { border-bottom: none; }
          .policy-summary-label { flex: 0 0 220px; font-weight: 500; color: #475569; }
          .policy-summary-value { flex: 1; color: #1e293b; word-break: break-word; }
          @media print { body { padding: 16px; } }
        </style>
      </head>
      <body>
        <div class="policy-summary-print-header">
          <h1>${escapeHtml(policyTypeLabel)}</h1>
          <div class="sub">Policy summary${policyName ? ` — ${escapeHtml(policyName)}` : ""}${effectiveDate ? ` | Effective: ${escapeHtml(effectiveDate)}` : ""}</div>
        </div>
        <div class="policy-summary-print-body">${bodyRows}</div>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}
